import { z } from 'zod';
import { catalogRequirementSchema } from './catalog-contracts';

const text = z.string().trim().min(1).max(600);
const reference = z.string().trim().min(1).max(500);
export const buyerEvidenceSchema = z
  .object({ messageId: reference, quote: text })
  .strict();
export const appointmentExpressionSchema = z
  .object({
    evidence: buyerEvidenceSchema,
    dateText: z.string().min(1).max(600).nullable(),
    timeText: z.string().min(1).max(600).nullable(),
    alternatives: z
      .array(
        z
          .object({
            dateText: z.string().min(1).max(600).nullable(),
            timeText: z.string().min(1).max(600).nullable(),
          })
          .strict()
      )
      .max(4)
      .nullable()
      .optional(),
    reset: z.boolean(),
  })
  .strict();
export type AppointmentExpression = z.infer<typeof appointmentExpressionSchema>;
export const salesQuestionSchema = buyerEvidenceSchema.extend({
  catalogScope: z
    .enum(['preferences', 'project', 'named_offerings', 'none'])
    .optional(),
  catalog: catalogRequirementSchema.nullable().optional(),
});
const classifiedBuyerMeaning = z
  .object({ id: z.string().min(1).max(100), evidence: buyerEvidenceSchema })
  .strict();

export const salesTurnUnderstandingSchema = z
  .object({
    focus: z.enum([
      'answer',
      'explore',
      'compare',
      'resolve_concern',
      'explain_call',
      'arrange',
      'decline',
    ]),
    catalogDisplay: z.enum(['none', 'shortlist', 'all']),
    motivation: classifiedBuyerMeaning.nullable(),
    concern: classifiedBuyerMeaning.nullable(),
    questions: z.array(salesQuestionSchema).max(8),
    referencedCatalogIds: z.array(reference).max(6),
    appointmentExpression: appointmentExpressionSchema.nullable().optional(),
    invitationResponse: z
      .object({
        disposition: z.enum([
          'not_now',
          'declined',
          'reopened',
          'coordinate',
          'requested',
        ]),
        evidence: buyerEvidenceSchema,
      })
      .strict()
      .nullable()
      .optional(),
  })
  .strict();
export type SalesTurnUnderstanding = z.infer<
  typeof salesTurnUnderstandingSchema
>;

export const salesArgumentSchema = z
  .object({
    buyerEvidenceRefs: z.array(reference).max(6),
    featureEvidence: z
      .array(
        z
          .object({
            factId: reference,
            fields: z.array(z.string().min(1).max(120)).min(1).max(6),
          })
          .strict()
      )
      .max(4),
    meaning: text.nullable(),
    comparedOfferingIds: z.array(reference).max(4),
    questionPurpose: text
      .nullable()
      .describe(
        'Only when the reply actually asks the buyer a discovery or clarification question: explain how their answer changes the recommendation. Null for statements, call invitations and scheduling.'
      ),
    adviserOutcomeId: z.string().min(1).max(100).nullable(),
    adviserAgenda: z.array(text).max(4),
    benefitId: z.string().min(1).max(100).nullable(),
  })
  .strict();
export type SalesArgument = z.infer<typeof salesArgumentSchema>;

export const salesAnswerCoverageSchema = z
  .array(
    z
      .object({
        questionIndex: z.number().int().min(0).max(7),
        status: z.enum([
          'supported',
          'unknown',
          'clarification',
          'read_required',
        ]),
        factIds: z.array(reference).max(8),
        answer: text,
      })
      .strict()
  )
  .max(8);
export type SalesAnswerCoverage = z.infer<typeof salesAnswerCoverageSchema>;

export const adviserDecisionSchema = z
  .object({
    outcomeId: z.string().min(1).max(100),
    agenda: z.array(text).max(4),
  })
  .strict();
